import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Shape the API sends on the `metadata` SSE event. Matches
 * `PreprocessResult['emotion']` in apps/api/src/services/interview.ts.
 */
export interface InterviewEmotion {
  emotionalTone: string;
  emotionalDensity: number;
  emotionalTags: string[];
  distressDetected: boolean;
  suggestedApproach?: string;
}

export interface InterviewMetadata {
  emotion: InterviewEmotion | null;
  piiDetected: boolean;
}

export interface InterviewStreamError {
  kind: 'http' | 'sse' | 'network';
  message: string;
  status?: number;
}

export interface UseInterviewStreamOptions {
  sessionId: string | null;
  threadId: string | null;
  /**
   * Admin "test as user" target id. When set, the hook includes it in the
   * POST body so the API scopes the interview to that user. Optional —
   * omit for a normal (non-impersonated) interview.
   */
  targetUserId?: string | null;
  /**
   * Phase 2 stream-lock token (phase2-interfaces.md §2.4). When provided,
   * the hook attaches it as `X-Stream-Lock` so the API's lock guard accepts
   * the streaming POST. Pass `null` for legacy traffic — the API skips the
   * guard when the session is not Phase 2.
   */
  lockToken?: string | null;
  /** Callback invoked when the API rejects the stream with a 409 lock error. */
  onLockLost?: () => void;
  onToken: (content: string) => void;
  onMetadata?: (metadata: InterviewMetadata) => void;
  onDone?: (messageId: string) => void;
}

export interface UseInterviewStreamResult {
  /**
   * Send a user turn. The optional `lockTokenOverride` exists to bridge the
   * React state-update race when the caller has *just* acquired a stream
   * lock and the hook's option closure still sees the pre-acquire null.
   * Pass the freshly-resolved token from `useStreamLock.acquire()` to this
   * arg; it takes precedence over the closure-captured `lockToken` for
   * this single call. Subsequent sends in the same session can rely on the
   * option (which will have re-rendered by then) and pass nothing.
   */
  send: (message: string, lockTokenOverride?: string | null) => Promise<void>;
  cancel: () => void;
  isStreaming: boolean;
  lastMetadata: InterviewMetadata | null;
  error: InterviewStreamError | null;
}

interface ParsedEvent {
  event: string;
  data: string;
}

/**
 * Parse a single SSE event block (lines separated by \n, block by \n\n).
 */
function parseSseBlock(block: string): ParsedEvent | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const colonIdx = line.indexOf(':');
    const field = colonIdx === -1 ? line : line.slice(0, colonIdx);
    const value = colonIdx === -1 ? '' : line.slice(colonIdx + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

/**
 * Hook that wraps fetch-based SSE streaming for the interview endpoint.
 *
 * Chosen over native EventSource because EventSource cannot POST a JSON body
 * or attach custom Authorization headers. ReadableStream + manual SSE parsing
 * keeps auth consistent with the rest of the app (Clerk bearer tokens).
 */
export function useInterviewStream(options: UseInterviewStreamOptions): UseInterviewStreamResult {
  const { sessionId, threadId, targetUserId, lockToken, onLockLost, onToken, onMetadata, onDone } =
    options;
  const { getToken } = useAuth();

  const [isStreaming, setIsStreaming] = useState(false);
  const [lastMetadata, setLastMetadata] = useState<InterviewMetadata | null>(null);
  const [error, setError] = useState<InterviewStreamError | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  // Keep latest callbacks in refs so send() doesn't need to be recreated on each render.
  const onTokenRef = useRef(onToken);
  const onMetadataRef = useRef(onMetadata);
  const onDoneRef = useRef(onDone);
  const onLockLostRef = useRef(onLockLost);
  useEffect(() => {
    onTokenRef.current = onToken;
    onMetadataRef.current = onMetadata;
    onDoneRef.current = onDone;
    onLockLostRef.current = onLockLost;
  }, [onToken, onMetadata, onDone, onLockLost]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  // Abort any in-flight stream on unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const send = useCallback(
    async (message: string, lockTokenOverride?: string | null) => {
      if (!sessionId || !threadId) {
        setError({ kind: 'http', message: 'No active session' });
        return;
      }

      // Cancel any previous in-flight stream.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);
      setLastMetadata(null);
      setIsStreaming(true);

      // `undefined` means "caller did not specify" → fall back to the
      // option-captured token. Explicit `null` means "no token, send
      // legacy" — distinct from the default and respected accordingly.
      const effectiveLockToken = lockTokenOverride !== undefined ? lockTokenOverride : lockToken;

      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/api/v1/interviews/message`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(effectiveLockToken ? { 'X-Stream-Lock': effectiveLockToken } : {}),
          },
          body: JSON.stringify({
            sessionId,
            threadId,
            message,
            ...(targetUserId ? { targetUserId } : {}),
          }),
        });

        if (!response.ok || !response.body) {
          // Phase 2 stream-lock errors arrive as 409 with a stable code in
          // the body (`stream_lock_mismatch` / `stream_lock_lost`). Surface
          // these to the caller via `onLockLost` so the UI can flip into
          // the takeover banner without a full re-render.
          if (response.status === 409) {
            try {
              const errBody = (await response.clone().json()) as {
                error?: { code?: string };
              };
              const code = errBody?.error?.code;
              if (code === 'stream_lock_mismatch' || code === 'stream_lock_lost') {
                onLockLostRef.current?.();
              }
            } catch {
              // ignore — we still set the http error below
            }
          }
          setError({
            kind: 'http',
            status: response.status,
            message: `Request failed with status ${response.status}`,
          });
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            // Flush any residual bytes so a final event without a terminating \n\n still parses.
            buffer += decoder.decode().replace(/\r\n/g, '\n');
            break;
          }
          // Normalize CRLF → LF so a single \n\n boundary check handles both.
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

          let sepIdx: number;
          while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, sepIdx);
            buffer = buffer.slice(sepIdx + 2);

            const parsed = parseSseBlock(rawEvent);
            if (!parsed) continue;

            try {
              const payload = JSON.parse(parsed.data);
              if (parsed.event === 'token' && typeof payload.content === 'string') {
                onTokenRef.current(payload.content);
              } else if (parsed.event === 'metadata') {
                // The server sends `emotion` as the full preprocess object
                // ({ emotionalTone, emotionalDensity, ... }). Cast narrowly —
                // we trust our own API's shape and validate at render time.
                const rawEmotion = payload.emotion as InterviewEmotion | null | undefined;
                const metadata: InterviewMetadata = {
                  emotion:
                    rawEmotion && typeof rawEmotion === 'object' && 'emotionalTone' in rawEmotion
                      ? rawEmotion
                      : null,
                  piiDetected: Boolean(payload.piiDetected),
                };
                setLastMetadata(metadata);
                onMetadataRef.current?.(metadata);
              } else if (parsed.event === 'done') {
                onDoneRef.current?.(payload.messageId);
              } else if (parsed.event === 'error') {
                setError({
                  kind: 'sse',
                  message: typeof payload.message === 'string' ? payload.message : 'Stream error',
                });
              }
            } catch {
              // Ignore malformed event payloads; the stream may still recover.
            }
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // User-initiated cancel; not an error state.
          return;
        }
        setError({
          kind: 'network',
          message: err instanceof Error ? err.message : 'Network error',
        });
      } finally {
        // Only clear state if we're still the current request. If a newer send()
        // superseded us (or cancel() ran), leave its state alone — otherwise the
        // losing send's finally would clobber the winner's isStreaming flag.
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsStreaming(false);
        }
      }
    },
    [sessionId, threadId, targetUserId, lockToken, getToken]
  );

  return { send, cancel, isStreaming, lastMetadata, error };
}
